import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  BookOpen,
  Upload,
  FileText,
  Trash2,
  Edit,
  ToggleLeft,
  ToggleRight,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Tag,
  CheckCircle,
  Clock,
  Archive,
  Eye,
  EyeOff,
  Download,
  File,
  FileCode,
  Image,
  X,
  ChevronDown,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

/**
 * AI Training Library Page
 * Upload and manage training documents for AI knowledge
 * Phase 1: Enhanced UI with visibility/status labels
 */

const AITrainingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [newDoc, setNewDoc] = useState({
    title: '',
    category: 'product',
    content_type: 'manual',
    text_content: '',
    tags: [],
    status: 'draft',
    visibility: 'internal'
  });
  const [newTag, setNewTag] = useState('');

  // Mock documents for Phase 1
  const mockDocuments = [
    {
      _id: 'doc_1',
      title: 'Credit Repair Process Overview',
      category: 'product',
      content_type: 'manual',
      text_content: 'Comprehensive guide to the credit repair process including dispute workflows, letter templates, and best practices for improving credit scores...',
      tags: ['credit-repair', 'disputes', 'workflow'],
      status: 'approved',
      visibility: 'public',
      enabled: true,
      created_at: new Date(Date.now() - 86400000 * 7).toISOString(),
      word_count: 2450
    },
    {
      _id: 'doc_2',
      title: 'Legal Compliance Guidelines',
      category: 'legal',
      content_type: 'manual',
      text_content: 'FCRA, FDCPA, and state-specific regulations governing credit repair services...',
      tags: ['legal', 'compliance', 'fcra'],
      status: 'approved',
      visibility: 'internal',
      enabled: true,
      created_at: new Date(Date.now() - 86400000 * 14).toISOString(),
      word_count: 3200
    },
    {
      _id: 'doc_3',
      title: 'API Integration Documentation',
      category: 'technical',
      content_type: 'manual',
      text_content: 'Technical documentation for integrating with IdentityIQ, credit bureaus, and third-party services...',
      tags: ['api', 'integration', 'technical'],
      status: 'draft',
      visibility: 'internal',
      enabled: false,
      created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
      word_count: 1800
    },
    {
      _id: 'doc_4',
      title: 'Client Onboarding FAQ',
      category: 'product',
      content_type: 'faq',
      text_content: 'Frequently asked questions about the onboarding process, pricing, and service expectations...',
      tags: ['faq', 'onboarding', 'clients'],
      status: 'approved',
      visibility: 'public',
      enabled: true,
      created_at: new Date(Date.now() - 86400000 * 5).toISOString(),
      word_count: 950
    },
    {
      _id: 'doc_5',
      title: 'Archived: Old Dispute Process',
      category: 'workflow',
      content_type: 'manual',
      text_content: 'Legacy dispute process documentation - superseded by new workflow...',
      tags: ['legacy', 'disputes'],
      status: 'archived',
      visibility: 'internal',
      enabled: false,
      created_at: new Date(Date.now() - 86400000 * 90).toISOString(),
      word_count: 1200
    }
  ];

  useEffect(() => {
    fetchDocuments();
  }, [filter, statusFilter]);

  const fetchDocuments = async () => {
    try {
      let url = '/api/admin/ai/training/documents?limit=100';
      if (filter !== 'all') {
        url += `&category=${filter}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents?.length > 0 ? data.documents : mockDocuments);
      } else {
        setDocuments(mockDocuments);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments(mockDocuments);
    } finally {
      setLoading(false);
    }
  };

  const createDocument = async () => {
    if (!newDoc.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      const response = await fetch('/api/admin/ai/training/documents', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newDoc)
      });
      
      if (response.ok) {
        toast.success('Document created');
        setShowUpload(false);
        resetNewDoc();
        fetchDocuments();
      } else {
        // Simulate success for Phase 1
        toast.success('Document created (Simulated)');
        const mockNewDoc = {
          _id: `doc_${Date.now()}`,
          ...newDoc,
          created_at: new Date().toISOString(),
          word_count: newDoc.text_content.split(' ').length
        };
        setDocuments(prev => [mockNewDoc, ...prev]);
        setShowUpload(false);
        resetNewDoc();
      }
    } catch (error) {
      console.error('Failed to create document:', error);
      toast.error('Failed to create document');
    }
  };

  const resetNewDoc = () => {
    setNewDoc({
      title: '',
      category: 'product',
      content_type: 'manual',
      text_content: '',
      tags: [],
      status: 'draft',
      visibility: 'internal'
    });
  };

  const toggleDocument = async (docId, enabled) => {
    toast.success(enabled ? 'Document disabled (Simulated)' : 'Document enabled (Simulated)');
    setDocuments(prev => prev.map(d => 
      d._id === docId ? { ...d, enabled: !enabled } : d
    ));
  };

  const updateDocumentStatus = (docId, newStatus) => {
    toast.success(`Status updated to ${newStatus} (Simulated)`);
    setDocuments(prev => prev.map(d =>
      d._id === docId ? { ...d, status: newStatus } : d
    ));
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    toast.success('Document deleted (Simulated)');
    setDocuments(prev => prev.filter(d => d._id !== docId));
    if (selectedDoc?._id === docId) {
      setSelectedDoc(null);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !newDoc.tags.includes(newTag.trim())) {
      setNewDoc(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setNewDoc(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      toast.info(`File "${file.name}" selected (Upload simulated in Phase 1)`);
      setNewDoc(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
    }
  };

  const categories = [
    { value: 'all', label: 'All Categories', icon: BookOpen },
    { value: 'product', label: 'Product', icon: FileText },
    { value: 'legal', label: 'Legal', icon: FileCode },
    { value: 'technical', label: 'Technical', icon: File },
    { value: 'workflow', label: 'Workflow', icon: Sparkles }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'approved', label: 'Approved' },
    { value: 'archived', label: 'Archived' }
  ];

  const contentTypes = [
    { value: 'manual', label: 'Manual / Guide' },
    { value: 'faq', label: 'FAQ' },
    { value: 'transcript', label: 'Transcript' },
    { value: 'pdf', label: 'PDF Upload' }
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Public', description: 'Visible to AI Assistant' },
    { value: 'internal', label: 'Internal', description: 'Admin & Dev Agent only' }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      archived: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    const icons = {
      draft: <Clock className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      archived: <Archive className="w-3 h-3" />
    };
    return (
      <Badge className={`${styles[status] || styles.draft} border text-xs flex items-center gap-1`}>
        {icons[status]}
        {status}
      </Badge>
    );
  };

  const getVisibilityBadge = (visibility) => {
    return visibility === 'public' ? (
      <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 text-xs flex items-center gap-1">
        <Eye className="w-3 h-3" />
        Public
      </Badge>
    ) : (
      <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-xs flex items-center gap-1">
        <EyeOff className="w-3 h-3" />
        Internal
      </Badge>
    );
  };

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.text_content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filter === 'all' || doc.category === filter;
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            AI Training Library
          </h1>
          <p className="text-slate-400 mt-1">
            Upload and manage training documents for AI knowledge
          </p>
        </div>
        <Button
          onClick={() => setShowUpload(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="add-training-doc-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Document
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Documents</p>
                <p className="text-2xl font-bold text-white">{documents.length}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Approved</p>
                <p className="text-2xl font-bold text-emerald-400">{documents.filter(d => d.status === 'approved').length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Drafts</p>
                <p className="text-2xl font-bold text-amber-400">{documents.filter(d => d.status === 'draft').length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Archived</p>
                <p className="text-2xl font-bold text-slate-400">{documents.filter(d => d.status === 'archived').length}</p>
              </div>
              <Archive className="w-8 h-8 text-slate-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="pl-10 bg-slate-800/50 border-slate-700 text-white"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={filter === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(cat.value)}
              className={filter === cat.value 
                ? 'bg-emerald-600 text-white' 
                : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
            >
              {cat.label}
            </Button>
          ))}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="p-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white text-sm"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-2xl mx-4 bg-[#0B1F18] border-[rgba(142,218,182,0.12)] max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Add Training Document</CardTitle>
                <CardDescription className="text-slate-400">
                  Upload a document for AI training
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowUpload(false); resetNewDoc(); }}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Drag & Drop Upload Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                  dragActive 
                    ? 'border-emerald-500 bg-emerald-500/10' 
                    : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                <Upload className={`w-10 h-10 mx-auto mb-3 ${dragActive ? 'text-emerald-400' : 'text-slate-500'}`} />
                <p className={`text-sm ${dragActive ? 'text-emerald-300' : 'text-slate-400'}`}>
                  Drag and drop a file here, or click to browse
                </p>
                <p className="text-xs text-slate-500 mt-1">Supports PDF, TXT, MD, DOCX</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 border-slate-600 text-slate-300"
                >
                  Browse Files
                </Button>
              </div>

              <div className="text-center text-sm text-slate-500">or enter content manually</div>

              <div>
                <Label className="text-slate-300">Title *</Label>
                <Input
                  value={newDoc.title}
                  onChange={(e) => setNewDoc(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Document title..."
                  className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Category</Label>
                  <select
                    value={newDoc.category}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, category: e.target.value }))}
                    className="mt-1 w-full p-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white"
                  >
                    {categories.slice(1).map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">Content Type</Label>
                  <select
                    value={newDoc.content_type}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, content_type: e.target.value }))}
                    className="mt-1 w-full p-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white"
                  >
                    {contentTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Status</Label>
                  <select
                    value={newDoc.status}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, status: e.target.value }))}
                    className="mt-1 w-full p-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="approved">Approved</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">Visibility</Label>
                  <select
                    value={newDoc.visibility}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, visibility: e.target.value }))}
                    className="mt-1 w-full p-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white"
                  >
                    {visibilityOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Content</Label>
                <Textarea
                  value={newDoc.text_content}
                  onChange={(e) => setNewDoc(prev => ({ ...prev, text_content: e.target.value }))}
                  placeholder="Paste document content here..."
                  rows={8}
                  className="mt-1 bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                />
              </div>

              <div>
                <Label className="text-slate-300">Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag..."
                    className="flex-1 bg-slate-800/50 border-slate-700 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button
                    onClick={addTag}
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {newDoc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newDoc.tags.map((tag, idx) => (
                      <Badge key={idx} className="bg-slate-700 text-slate-300 flex items-center gap-1">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-700/50">
                <Button
                  variant="outline"
                  onClick={() => { setShowUpload(false); resetNewDoc(); }}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createDocument}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Add Document
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Documents Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Documents List */}
        <div className="lg:col-span-2">
          <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Documents ({filteredDocs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : filteredDocs.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-400">No training documents found.</p>
                  <p className="text-sm text-slate-500 mt-1">Click "Add Document" to create one.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredDocs.map((doc) => (
                    <div
                      key={doc._id}
                      onClick={() => setSelectedDoc(doc)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedDoc?._id === doc._id
                          ? 'border-emerald-500/50 bg-emerald-500/5'
                          : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getStatusBadge(doc.status)}
                          {getVisibilityBadge(doc.visibility)}
                          <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                            {doc.category}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.enabled !== false ? (
                            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs">
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-500/20 text-slate-400 border border-slate-500/30 text-xs">
                              Disabled
                            </Badge>
                          )}
                        </div>
                      </div>
                      <h3 className="font-medium text-white">{doc.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                        {doc.text_content?.substring(0, 150)}...
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>{doc.word_count || 0} words</span>
                        <span>{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}</span>
                        {doc.tags?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {doc.tags.length} tags
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Document Details Panel */}
        <div>
          {selectedDoc ? (
            <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)] sticky top-6">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white">Document Details</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedDoc(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge(selectedDoc.status)}
                  {getVisibilityBadge(selectedDoc.visibility)}
                </div>

                <div>
                  <p className="text-sm text-slate-500">Title</p>
                  <p className="font-medium text-white">{selectedDoc.title}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Category</p>
                    <p className="text-slate-300">{selectedDoc.category}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Type</p>
                    <p className="text-slate-300">{selectedDoc.content_type}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500">Tags</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedDoc.tags?.map((tag, idx) => (
                      <Badge key={idx} className="bg-slate-700 text-slate-300 text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">Preview</p>
                  <div className="p-3 rounded bg-slate-800/50 text-xs text-slate-400 max-h-32 overflow-y-auto">
                    {selectedDoc.text_content?.substring(0, 500)}...
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-700/50 space-y-3">
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Update Status</p>
                    <div className="flex gap-2">
                      {['draft', 'approved', 'archived'].map((status) => (
                        <Button
                          key={status}
                          size="sm"
                          variant={selectedDoc.status === status ? 'default' : 'outline'}
                          onClick={() => updateDocumentStatus(selectedDoc._id, status)}
                          className={selectedDoc.status === status 
                            ? 'bg-emerald-600 text-white' 
                            : 'border-slate-600 text-slate-300 text-xs'}
                        >
                          {status}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleDocument(selectedDoc._id, selectedDoc.enabled !== false)}
                      className="flex-1 border-slate-600 text-slate-300"
                    >
                      {selectedDoc.enabled !== false ? (
                        <>
                          <ToggleRight className="w-4 h-4 mr-1 text-emerald-400" />
                          Enabled
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-4 h-4 mr-1" />
                          Disabled
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteDocument(selectedDoc._id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">Select a document to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AITrainingPage;
